import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { UserRewards, RewardTransaction, ReferralCode, Referral } from '../types'

export default function ReferralDashboard() {
  const { user, isLoggedIn } = useAuth()
  const [userRewards, setUserRewards] = useState<UserRewards | null>(null)
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null)
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [transactions, setTransactions] = useState<RewardTransaction[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'referrals' | 'transactions'>('overview')

  useEffect(() => {
    if (!isLoggedIn || !user) return

    // Mock data - in real app this would come from API
    const mockUserRewards: UserRewards = {
      userId: user.id,
      totalEarned: 4550,
      currentBalance: 3225,
      totalRedeemed: 1325,
      referralCount: 2,
      tier: 'bronze',
      tierProgress: 2,
      nextTierRequirement: 5
    }

    const mockReferralCode: ReferralCode = {
      id: 'ref-1',
      userId: user.id,
      code: `KRATOM${user.username.toUpperCase()}`,
      isActive: true,
      createdDate: new Date('2024-01-01'),
      currentUsage: 3
    }

    const mockReferrals: Referral[] = [
      {
        id: 'ref-1',
        referrerId: user.id,
        refereeId: 'user-10',
        referralCode: mockReferralCode.code,
        status: 'completed',
        dateReferred: new Date('2024-01-15'),
        dateCompleted: new Date('2024-01-16'),
        rewardAwarded: true,
        rewardAmount: 1000
      },
      {
        id: 'ref-2',
        referrerId: user.id,
        refereeId: 'user-11',
        referralCode: mockReferralCode.code,
        status: 'completed',
        dateReferred: new Date('2024-01-20'),
        dateCompleted: new Date('2024-01-22'),
        rewardAwarded: true,
        rewardAmount: 1000
      },
      {
        id: 'ref-3',
        referrerId: user.id,
        refereeId: 'user-12',
        referralCode: mockReferralCode.code,
        status: 'pending',
        dateReferred: new Date('2024-01-25'),
        rewardAwarded: false
      }
    ]

    const mockTransactions: RewardTransaction[] = [
      {
        id: 'tx-1',
        userId: user.id,
        type: 'signup_bonus',
        amount: 500,
        description: 'Welcome bonus for joining',
        date: new Date('2024-01-01'),
        status: 'completed'
      },
      {
        id: 'tx-2',
        userId: user.id,
        type: 'referral_bonus',
        amount: 1000,
        description: 'Referral bonus for KratomUser123',
        date: new Date('2024-01-16'),
        status: 'completed',
        relatedId: 'ref-1'
      },
      {
        id: 'tx-3',
        userId: user.id,
        type: 'review_reward',
        amount: 250,
        description: 'Reward for verified review',
        date: new Date('2024-01-18'),
        status: 'completed'
      },
      {
        id: 'tx-4',
        userId: user.id,
        type: 'referral_bonus',
        amount: 1000,
        description: 'Referral bonus for PlantLover88',
        date: new Date('2024-01-22'),
        status: 'completed',
        relatedId: 'ref-2'
      },
      {
        id: 'tx-5',
        userId: user.id,
        type: 'redemption',
        amount: -1325,
        description: 'Redeemed for gift card',
        date: new Date('2024-01-23'),
        status: 'completed'
      }
    ]

    setUserRewards(mockUserRewards)
    setReferralCode(mockReferralCode)
    setReferrals(mockReferrals)
    setTransactions(mockTransactions)
  }, [user, isLoggedIn])

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode.code)
      alert('Referral code copied to clipboard!')
    }
  }

  const generateReferralLink = () => {
    if (referralCode) {
      const link = `${window.location.origin}?ref=${referralCode.code}`
      navigator.clipboard.writeText(link)
      alert('Referral link copied to clipboard!')
    }
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return '#CD7F32'
      case 'silver': return '#C0C0C0'
      case 'gold': return '#FFD700'
      case 'platinum': return '#E5E4E2'
      default: return '#CD7F32'
    }
  }

  const formatPoints = (amount: number) => {
    return `${amount.toLocaleString()} points`
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date)
  }

  if (!isLoggedIn) {
    return (
      <div className="referral-dashboard">
        <div className="login-required">
          <h2>Login Required</h2>
          <p>Please log in to access your referral dashboard.</p>
        </div>
      </div>
    )
  }

  if (!userRewards || !referralCode) {
    return (
      <div className="referral-dashboard">
        <div className="loading">Loading your rewards...</div>
      </div>
    )
  }

  return (
    <div className="referral-dashboard">
      <div className="dashboard-header">
        <h1>Referral & Rewards Dashboard</h1>
        <p>Earn rewards by referring friends and engaging with our community!</p>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'referrals' ? 'active' : ''}`}
          onClick={() => setActiveTab('referrals')}
        >
          My Referrals
        </button>
        <button 
          className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transaction History
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-tab">
          <div className="rewards-summary">
            <div className="balance-card">
              <h3>Current Balance</h3>
              <div className="balance-amount">{formatPoints(userRewards.currentBalance)}</div>
              <button className="redeem-btn">Redeem Rewards</button>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <h4>Total Earned</h4>
                <div className="stat-value">{formatPoints(userRewards.totalEarned)}</div>
              </div>
              <div className="stat-card">
                <h4>Total Redeemed</h4>
                <div className="stat-value">{formatPoints(userRewards.totalRedeemed)}</div>
              </div>
              <div className="stat-card">
                <h4>Successful Referrals</h4>
                <div className="stat-value">{userRewards.referralCount}</div>
              </div>
            </div>
          </div>

          <div className="tier-progress">
            <div className="tier-header">
              <h3>Your Tier: <span style={{ color: getTierColor(userRewards.tier) }}>{userRewards.tier.toUpperCase()}</span></h3>
              <p>Progress to next tier: {userRewards.tierProgress}/{userRewards.nextTierRequirement} referrals</p>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${(userRewards.tierProgress / userRewards.nextTierRequirement) * 100}%`,
                  background: getTierColor(userRewards.tier)
                }}
              ></div>
            </div>
          </div>

          <div className="referral-tools">
            <h3>Share Your Referral Code</h3>
            <div className="referral-code-section">
              <div className="code-display">
                <span className="code">{referralCode.code}</span>
                <button onClick={copyReferralCode} className="copy-btn">Copy Code</button>
              </div>
              <button onClick={generateReferralLink} className="share-btn">
                Copy Referral Link
              </button>
            </div>
            
            <div className="share-info">
              <h4>How it works:</h4>
              <ul>
                <li>Share your referral code or link with friends</li>
                <li>They sign up using your code</li>
                <li>You both earn rewards when they make their first purchase</li>
                <li>Keep earning as you refer more people!</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'referrals' && (
        <div className="referrals-tab">
          <div className="referrals-header">
            <h3>Your Referrals ({referrals.length})</h3>
            <p>Track the status of people you've referred</p>
          </div>

          <div className="referrals-list">
            {referrals.length === 0 ? (
              <div className="no-referrals">
                <p>You haven't referred anyone yet. Start sharing your code to earn rewards!</p>
              </div>
            ) : (
              referrals.map((referral) => (
                <div key={referral.id} className={`referral-card ${referral.status}`}>
                  <div className="referral-info">
                    <div className="referral-date">
                      Referred: {formatDate(referral.dateReferred)}
                    </div>
                    <div className="referral-status">
                      Status: <span className={`status ${referral.status}`}>{referral.status}</span>
                    </div>
                    {referral.dateCompleted && (
                      <div className="completion-date">
                        Completed: {formatDate(referral.dateCompleted)}
                      </div>
                    )}
                  </div>
                  <div className="referral-reward">
                    {referral.rewardAwarded ? (
                      <div className="reward-earned">
                        <span className="reward-amount">+{formatPoints(referral.rewardAmount || 0)}</span>
                        <span className="reward-label">Earned</span>
                      </div>
                    ) : (
                      <div className="reward-pending">
                        <span className="reward-label">Pending</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="transactions-tab">
          <div className="transactions-header">
            <h3>Transaction History</h3>
            <p>View all your reward earnings and redemptions</p>
          </div>

          <div className="transactions-list">
            {transactions.map((transaction) => (
              <div key={transaction.id} className={`transaction-card ${transaction.type}`}>
                <div className="transaction-info">
                  <div className="transaction-description">{transaction.description}</div>
                  <div className="transaction-date">{formatDate(transaction.date)}</div>
                  <div className="transaction-type">{transaction.type.replace('_', ' ')}</div>
                </div>
                <div className="transaction-amount">
                  <span className={transaction.amount > 0 ? 'positive' : 'negative'}>
                    {transaction.amount > 0 ? '+' : ''}{formatPoints(Math.abs(transaction.amount))}
                  </span>
                  <div className={`status ${transaction.status}`}>{transaction.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .referral-dashboard {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          background: #1a202c;
          min-height: 100vh;
          color: #e2e8f0;
        }

        .dashboard-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .dashboard-header h1 {
          color: #00d166;
          margin-bottom: 0.5rem;
          font-size: 2.5rem;
        }

        .dashboard-header p {
          color: #a0aec0;
          font-size: 1.1rem;
        }

        .dashboard-tabs {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          border-bottom: 1px solid #4a5568;
        }

        .tab-btn {
          background: none;
          border: none;
          color: #a0aec0;
          padding: 1rem 1.5rem;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .tab-btn:hover {
          color: #e2e8f0;
        }

        .tab-btn.active {
          color: #00d166;
          border-bottom-color: #00d166;
        }

        .rewards-summary {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .balance-card {
          background: #2d3748;
          padding: 2rem;
          border-radius: 8px;
          text-align: center;
          border: 1px solid #4a5568;
        }

        .balance-card h3 {
          color: #a0aec0;
          margin: 0 0 1rem 0;
          font-size: 1.125rem;
        }

        .balance-amount {
          font-size: 3rem;
          font-weight: bold;
          color: #00d166;
          margin-bottom: 1.5rem;
        }

        .redeem-btn {
          background: #00d166;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .redeem-btn:hover {
          background: #4caf50;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .stat-card {
          background: #2d3748;
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid #4a5568;
          text-align: center;
        }

        .stat-card h4 {
          color: #a0aec0;
          margin: 0 0 0.5rem 0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          color: #e2e8f0;
        }

        .tier-progress {
          background: #2d3748;
          padding: 2rem;
          border-radius: 8px;
          border: 1px solid #4a5568;
          margin-bottom: 2rem;
        }

        .tier-header h3 {
          margin: 0 0 0.5rem 0;
          color: #e2e8f0;
        }

        .tier-header p {
          color: #a0aec0;
          margin: 0 0 1rem 0;
        }

        .progress-bar {
          background: #1a202c;
          height: 12px;
          border-radius: 6px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 6px;
          transition: width 0.3s ease;
        }

        .referral-tools {
          background: #2d3748;
          padding: 2rem;
          border-radius: 8px;
          border: 1px solid #4a5568;
        }

        .referral-tools h3 {
          color: #e2e8f0;
          margin: 0 0 1.5rem 0;
        }

        .referral-code-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .code-display {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: #1a202c;
          padding: 1rem;
          border-radius: 6px;
          border: 1px solid #4a5568;
        }

        .code {
          font-family: monospace;
          font-size: 1.25rem;
          font-weight: bold;
          color: #00d166;
          letter-spacing: 1px;
        }

        .copy-btn, .share-btn {
          background: #4a5568;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .copy-btn:hover, .share-btn:hover {
          background: #68d391;
        }

        .share-info h4 {
          color: #e2e8f0;
          margin: 0 0 1rem 0;
        }

        .share-info ul {
          color: #a0aec0;
          padding-left: 1.5rem;
        }

        .share-info li {
          margin-bottom: 0.5rem;
        }

        .referrals-header, .transactions-header {
          margin-bottom: 2rem;
        }

        .referrals-header h3, .transactions-header h3 {
          color: #e2e8f0;
          margin: 0 0 0.5rem 0;
        }

        .referrals-header p, .transactions-header p {
          color: #a0aec0;
          margin: 0;
        }

        .referral-card, .transaction-card {
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .referral-card.completed {
          border-left: 4px solid #48bb78;
        }

        .referral-card.pending {
          border-left: 4px solid #ed8936;
        }

        .referral-info, .transaction-info {
          flex: 1;
        }

        .referral-date, .transaction-date {
          color: #a0aec0;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .referral-status, .transaction-type {
          font-size: 0.875rem;
          color: #e2e8f0;
          margin-bottom: 0.25rem;
        }

        .status.completed {
          color: #48bb78;
          font-weight: 600;
        }

        .status.pending {
          color: #ed8936;
          font-weight: 600;
        }

        .reward-earned {
          text-align: right;
        }

        .reward-amount {
          color: #48bb78;
          font-size: 1.25rem;
          font-weight: bold;
          display: block;
        }

        .reward-label {
          color: #a0aec0;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .reward-pending .reward-label {
          color: #ed8936;
        }

        .transaction-amount {
          text-align: right;
        }

        .transaction-amount .positive {
          color: #48bb78;
        }

        .transaction-amount .negative {
          color: #f56565;
        }

        .transaction-amount span {
          font-size: 1.25rem;
          font-weight: bold;
          display: block;
        }

        .transaction-amount .status {
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .no-referrals {
          text-align: center;
          padding: 3rem;
          color: #a0aec0;
        }

        .login-required, .loading {
          text-align: center;
          padding: 3rem;
        }

        .login-required h2, .loading {
          color: #00d166;
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .referral-dashboard {
            padding: 1rem;
          }

          .dashboard-tabs {
            flex-direction: column;
          }

          .rewards-summary {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .referral-code-section {
            flex-direction: column;
          }

          .referral-card, .transaction-card {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }

          .reward-earned, .transaction-amount {
            text-align: left;
          }
        }
      `}</style>
    </div>
  )
}